import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { styled, keyframes } from '@mui/material/styles';
import { Box, IconButton, Stack, Typography } from '@mui/material';
// components
import Page from '../Page';
import Iconify from '../Iconify';
import StoreBadges from '../StoreBadges';
// config
import { APP_NAME, GRADIENT_CONFIG } from '../../config';

// ---------------------------------------------------------------------
// AuthShell — premium split: a rich brand story (headline, features,
// trust stats, floating app-preview card) on the left, a clean form on
// the right. Mobile collapses to just the form. Shared by Login/Register.
// ---------------------------------------------------------------------

const float = keyframes`
  0%,100% { transform: translateY(0) rotate(-3deg); }
  50%     { transform: translateY(-14px) rotate(-3deg); }
`;
const glow = keyframes`
  0%,100% { opacity: .5; transform: scale(1); }
  50%     { opacity: .8; transform: scale(1.08); }
`;

const Root = styled('main')(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  maxWidth: '100vw',
  overflowX: 'hidden',
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1fr)',
  background: theme.palette.background.paper,
  [theme.breakpoints.up('md')]: { gridTemplateColumns: 'minmax(0,52fr) minmax(0,48fr)' },
}));

/* ---------------- LEFT brand panel (desktop) ---------------- */
const Brand = styled('aside')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: '100vh',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(7, 6),
    background: `linear-gradient(155deg, ${GRADIENT_CONFIG.dark} 0%, ${GRADIENT_CONFIG.primary} 58%, ${GRADIENT_CONFIG.tertiary} 100%)`,
  },
}));

const Dots = styled('div')({
  position: 'absolute',
  inset: 0,
  opacity: 0.1,
  backgroundImage: 'radial-gradient(circle at center, #fff 1px, transparent 1.6px)',
  backgroundSize: '26px 26px',
  pointerEvents: 'none',
});
const Glow = styled('div')({
  position: 'absolute',
  width: 420,
  height: 420,
  top: -140,
  right: -120,
  borderRadius: '50%',
  background: 'rgba(16,185,129,0.5)',
  filter: 'blur(120px)',
  animation: `${glow} 7s ease-in-out infinite`,
  pointerEvents: 'none',
});

/* floating app-preview card */
const Mock = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 300,
  maxWidth: '100%',
  margin: theme.spacing(4, 0),
  background: '#fff',
  color: theme.palette.text.primary,
  borderRadius: 22,
  padding: theme.spacing(2.5),
  boxShadow: '0 30px 60px -20px rgba(0,0,0,0.5)',
  animation: `${float} 6s ease-in-out infinite`,
}));
const MockGhost = styled('div')({
  position: 'absolute',
  inset: 0,
  transform: 'rotate(5deg) translate(14px, 12px)',
  background: 'rgba(255,255,255,0.14)',
  borderRadius: 22,
  zIndex: -1,
});

/* ---------------- RIGHT form ---------------- */
const FormSide = styled('section')(({ theme }) => ({
  minWidth: 0,
  boxSizing: 'border-box',
  padding: theme.spacing(4, 2.5, 6),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(6, 5) },
  [theme.breakpoints.up('md')]: { justifyContent: 'center', padding: theme.spacing(6, 7) },
}));
const Inner = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 420,
  paddingTop: theme.spacing(1),
  [theme.breakpoints.up('sm')]: { paddingTop: theme.spacing(3) },
}));
const BackBtn = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  '&:hover': { background: theme.palette.action.hover },
}));

const FEATURES = [
  'Instant data, airtime & bill payments',
  'Airtime to cash, eSIM & more',
  'Bank-grade security, always',
];
const STATS = [
  ['10k+', 'Active users'],
  ['₦2B+', 'Processed'],
  ['4.8★', 'App rating'],
];
const ACTIONS = [
  ['eva:wifi-fill', 'Data'],
  ['eva:smartphone-fill', 'Airtime'],
  ['eva:flash-fill', 'Bills'],
];

AuthShell.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  pageTitle: PropTypes.string,
  backTo: PropTypes.string,
  showBack: PropTypes.bool,
};

export default function AuthShell({ title, subtitle, children, footer, pageTitle, backTo, showBack = true }) {
  const navigate = useNavigate();
  const onBack = () => (backTo ? navigate(backTo) : navigate(-1));

  return (
    <Page title={pageTitle || title} sx={{ height: 1 }}>
      <Root>
        {/* LEFT */}
        <Brand>
          <Dots />
          <Glow />

          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative', zIndex: 2 }}>
            <Box component="img" src="/app_icon.png" alt="BetaData" sx={{ width: 40, height: 40, borderRadius: '11px' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
              {APP_NAME}
            </Typography>
          </Stack>

          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography sx={{ fontWeight: 800, letterSpacing: '-1.2px', lineHeight: 1.05, fontSize: { md: '2.6rem', lg: '3rem' }, color: '#fff' }}>
              Everything you pay for,<br />in one place.
            </Typography>

            {/* app-preview card */}
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <MockGhost />
              <Mock>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>Wallet balance</Typography>
                  <Box sx={{ px: 1, py: 0.25, borderRadius: 999, bgcolor: 'primary.lighter', color: 'primary.dark', fontSize: 11, fontWeight: 800 }}>+12%</Box>
                </Stack>
                <Typography sx={{ mt: 0.5, fontWeight: 800, fontSize: '1.7rem', letterSpacing: '-0.5px' }}>₦125,400<Box component="span" sx={{ fontSize: '1rem', color: 'text.secondary' }}>.00</Box></Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {ACTIONS.map(([ic, l]) => (
                    <Stack key={l} alignItems="center" spacing={0.75} sx={{ flex: 1, py: 1.25, borderRadius: 2.5, bgcolor: 'grey.100' }}>
                      <Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'primary.lighter', color: 'primary.main' }}>
                        <Iconify icon={ic} width={17} height={17} />
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{l}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Mock>
            </Box>

            <Stack spacing={1.25} sx={{ mt: 1 }}>
              {FEATURES.map((f) => (
                <Stack key={f} direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
                    <Iconify icon="eva:checkmark-fill" width={13} height={13} sx={{ color: '#fff' }} />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.95rem', fontWeight: 600 }}>{f}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={4} sx={{ position: 'relative', zIndex: 2 }}>
            {STATS.map(([n, l]) => (
              <Box key={l}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#fff', lineHeight: 1 }}>{n}</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>{l}</Typography>
              </Box>
            ))}
          </Stack>
        </Brand>

        {/* RIGHT */}
        <FormSide>
          <Inner>
            {showBack && (
              <BackBtn onClick={onBack} aria-label="Back">
                <Iconify icon="eva:arrow-ios-back-fill" width={20} height={20} />
              </BackBtn>
            )}

            {/* app icon shows on mobile (where the brand panel is hidden) */}
            <Box
              component="img"
              src="/app_icon.png"
              alt="BetaData"
              sx={{ width: 76, height: 76, borderRadius: '20px', objectFit: 'cover', display: { xs: 'block', md: 'none' }, mx: 'auto', mb: 3, boxShadow: '0 10px 26px -10px rgba(0,0,0,0.35)' }}
            />

            <Typography
              sx={{
                textAlign: { xs: 'center', md: 'left' },
                fontWeight: 800,
                letterSpacing: '-0.6px',
                lineHeight: 1.15,
                fontSize: { xs: '1.8rem', sm: '2rem' },
                color: 'text.primary',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ mt: 1.25, textAlign: { xs: 'center', md: 'left' }, fontSize: '1rem', lineHeight: 1.5, color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}

            <Box sx={{ mt: 4 }}>{children}</Box>

            {footer && <Box sx={{ mt: 3, textAlign: 'center' }}>{footer}</Box>}

            <Box sx={{ mt: 4 }}>
              <StoreBadges caption="Prefer the app? Get BetaData on:" />
            </Box>
          </Inner>
        </FormSide>
      </Root>
    </Page>
  );
}
